<template>
  <WarRoomCommissionEarnBannerCard>
    <div slot="header" class="war-room-commission-earn-banner-checklist__header">
      Earn Commission
      <QuestionIcon class="war-room-commission-earn-banner-checklist__icon" @click="openFAQ" />
    </div>
    <div slot="content">
      <ul class="war-room-commission-earn-banner-checklist__items">
        <li
          v-for="{ price, type, isComplete } in visibleItems"
          :key="type"
          class="war-room-commission-earn-banner-checklist__item"
          data-test="item-block"
        >
          <BravadoCheckboxV3 size="sm" :disabled="true" :checked="isComplete">
            <span
              class="war-room-commission-earn-banner-checklist__item-label"
              :class="{ 'war-room-commission-earn-banner-checklist__item-label--complete': isComplete }"
              data-test="item-label-block"
            >
              <template v-if="type === commissionItemTypes.SIGN_UP">Sign Up</template>
              <template v-else-if="type === commissionItemTypes.UPLOAD_PROFILE_PICTURE">
                <span class="war-room-commission-earn-banner-checklist__invite" @click="onUploadAvatarClick">
                  Upload
                </span>
                an avatar
              </template>
              <template v-else-if="type === commissionItemTypes.READ_FAQ">
                <NuxtLink to="/war-room/posts/faq" @click.native="handleItemClick(type)">Read</NuxtLink>
                our FAQ
              </template>
              <template v-else-if="type === commissionItemTypes.UPVOTE_POST_OR_COMMENT">Upvote a thread</template>
              <template v-else-if="type === commissionItemTypes.CREATE_COMMENT">Respond to a thread</template>
              <template v-else-if="type === commissionItemTypes.FILL_PROFILE">Complete my Portfolio</template>
              <template v-else-if="type === commissionItemTypes.DOWNLOAD_MOBILE_APP">Download mobile app</template>
            </span>
          </BravadoCheckboxV3>
          <WarRoomCommissionEarnBannerPoint
            :count="price"
            class="war-room-commission-earn-banner-checklist__item-point"
          />
        </li>
      </ul>
    </div>
  </WarRoomCommissionEarnBannerCard>
</template>

<script lang="ts" setup>
import { computed, onMounted, useContext, useRouter } from '@nuxtjs/composition-api';
import BravadoCheckboxV3 from '@/components/common/BravadoCheckboxV3/BravadoCheckboxV3.vue';
import WarRoomCommissionEarnBannerCard from '@/components/partials/war-room/WarRoomCommissionEarnBanner/WarRoomCommissionEarnBannerCard.vue';
import WarRoomCommissionEarnBannerPoint from '@/components/partials/war-room/WarRoomCommissionEarnBanner/WarRoomCommissionEarnBannerPoint.vue';
import QuestionIcon from '@/assets/svg/question.vue.svg';
import { commissionItemTypes, FAQPostUri } from '@/components/partials/war-room/WarRoomCommissionEarnBanner/constants';

type CommissionChecklistNames = (typeof commissionItemTypes)[keyof typeof commissionItemTypes];

interface CommissionChecklistItem {
  type: string;
  price: number;
  state: 'new' | 'done';
  isComplete?: boolean;
}

type WarRoomCommissionEarnBannerChecklistProps = {
  items: CommissionChecklistItem[];
};

const props = defineProps<WarRoomCommissionEarnBannerChecklistProps>();
const { $analytics } = useContext();
const router = useRouter();

const ALLOWED_TYPES = [
  commissionItemTypes.SIGN_UP,
  commissionItemTypes.UPLOAD_PROFILE_PICTURE,
  commissionItemTypes.READ_FAQ,
  commissionItemTypes.UPVOTE_POST_OR_COMMENT,
  commissionItemTypes.CREATE_COMMENT,
  commissionItemTypes.FILL_PROFILE,
  commissionItemTypes.DOWNLOAD_MOBILE_APP,
];

onMounted(() => {
  $analytics?.logEvent('war-room__all-posts__checklist__shown');
});

const visibleItems = computed(() => {
  const itemsMap = props.items.reduce(
    (items, item) => ({ ...items, [item.type]: { ...item, isComplete: item.state === 'done' } }),
    {},
  ) as Record<CommissionChecklistNames, CommissionChecklistItem>;

  return ALLOWED_TYPES.reduce(
    (items: CommissionChecklistItem[], type: CommissionChecklistNames) =>
      itemsMap[type] ? [...items, itemsMap[type]] : items,
    [],
  );
});

function openFAQ() {
  router.push(FAQPostUri);
}

function handleItemClick(type: string) {
  $analytics?.logEvent('war-room__all-posts__checklist-action__click', { action: type });
}

async function onUploadAvatarClick() {
  handleItemClick(commissionItemTypes.UPLOAD_PROFILE_PICTURE);

  await router.push('/war-room/profile/edit');
}
</script>

<style scoped lang="scss">
.war-room-commission-earn-banner-checklist {
  $this: &;

  &__header {
    height: 36px;

    @include flex-center;

    color: var(--blue-60);
    background: var(--blue-60-a20);

    @include subhead-semibold;

    padding: 0 8px;
  }

  &__icon {
    color: var(--blue-60);
    cursor: pointer;
    margin-left: 4px;
    transition: color 0.3s;

    &:hover {
      color: var(--blue-50);
    }
  }

  &__items {
    list-style-type: none;
    margin: 0;
    padding: 0;
    margin-top: 8px;
  }

  &__item {
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    ::v-deep .checkbox-v2__label {
      cursor: auto;
    }

    ::v-deep .checkbox-v2__text:hover {
      &::before {
        border-color: var(--theme-color-border);
      }
    }
  }

  &__item-point {
    color: var(--theme-color-text);
    min-width: 42px;
  }

  &__item-label {
    &--complete {
      text-decoration: line-through;

      #{$this} {
        &__invite {
          text-decoration: none;
          cursor: default;
          pointer-events: none;
        }
      }
    }
  }

  &__invite {
    text-decoration: underline;
    cursor: pointer;
  }
}
</style>
